/**
 * Helpers for the admin user-details auth/login-activity views (SRF-1463):
 * login history + linked social providers + last-active.
 *
 * All self-contained — no external deps. Country flags are rendered from the
 * ISO 3166-1 alpha-2 code via Unicode regional-indicator symbols (no image assets).
 */

/** Absolute date-time, e.g. "Jul 25, 2026, 02:14 PM". Null-safe. */
export const formatDateTime = (epochMs?: number): string => {
    if (!epochMs) return "-";
    // toLocaleString (not toLocaleDateString) — the date-only variant ignores hour/minute
    // options in many runtimes, so the time would be dropped from the tooltip.
    return new Date(epochMs).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

/** Relative time, e.g. "just now", "5m ago", "3h ago", "2d ago", else absolute date. */
export const formatRelativeTime = (epochMs?: number): string => {
    if (!epochMs) return "-";
    const diffMs = Date.now() - epochMs;
    if (diffMs < 0) return formatDateTime(epochMs);
    const sec = Math.floor(diffMs / 1000);
    if (sec < 45) return "just now";
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const days = Math.floor(hr / 24);
    if (days < 7) return `${days}d ago`;
    return formatDateTime(epochMs);
};

/**
 * Emoji flag for an ISO 3166-1 alpha-2 country code (e.g. "NL" → 🇳🇱).
 * Returns null for missing/invalid codes so callers can fall back to showing the raw code.
 */
export const countryFlagEmoji = (code?: string): string | null => {
    if (!code || code.length !== 2) return null;
    const upper = code.toUpperCase();
    if (!/^[A-Z]{2}$/.test(upper)) return null;
    // Each letter → regional-indicator symbol (U+1F1E6 is 'A').
    const base = 0x1f1e6;
    return (
        String.fromCodePoint(base + (upper.charCodeAt(0) - 65)) +
        String.fromCodePoint(base + (upper.charCodeAt(1) - 65))
    );
};

/** Human label for an auth_event.event_type value. */
export const eventTypeLabel = (eventType?: string): string => {
    switch (eventType) {
        case "login":
            return "Login";
        case "signup":
            return "Signup";
        case "link":
            return "Linked";
        case "unlink":
            return "Unlinked";
        case "login_failed":
            return "Login failed";
        default:
            return eventType || "-";
    }
};

/** Tailwind badge classes for an auth_event.event_type value. */
export const eventTypeBadgeClasses = (eventType?: string): string => {
    switch (eventType) {
        case "login":
        case "signup":
            return "bg-green-100 text-green-800";
        case "link":
            return "bg-blue-100 text-blue-800";
        case "unlink":
            return "bg-amber-100 text-amber-800";
        case "login_failed":
            return "bg-red-100 text-red-800";
        default:
            return "bg-gray-100 text-gray-800";
    }
};

/** Human label for an auth method (google/apple/otp/password/admin/watch). */
export const methodLabel = (method?: string): string => {
    switch (method) {
        case "google":
            return "Google";
        case "apple":
            return "Apple";
        case "otp":
            return "Email code";
        case "password":
            return "Password";
        case "admin":
            return "Admin";
        case "watch":
            return "Watch";
        default:
            return method || "-";
    }
};
